import { SWATCH_PREFIX } from '../constants/filter-tree';
import { getThemeSettings, handleClickOutside, safeParseJSON } from '../utils';

const SWATCH_OPTION_TYPE = {
  PRODUCT_IMAGE: 'product-image',
  COLOR_IMAGE: 'color-and-image',
  TEXT: 'text',
};
/**
 * @type {import('./index.d').handleProductSwatches}
 */
export const handleProductSwatches = (context) => {
  const { elements } = context.app.templateSettings?.themeSettings?.productItems?.productInfo || {};
  const swatchesEnabled =
    elements?.['swatches.1']?.isEnable ||
    elements?.['swatches.2']?.isEnable ||
    elements?.['swatches.3']?.isEnable;

  if (swatchesEnabled) {
    const swatches = getEnabledSwatches(context);

    // Get all product items of a block
    const productItems = Array.from(
      context.document?.querySelectorAll(
        '.boost-sd__product-item, .boost-sd__product-item-list-view-layout'
      )
    );

    productItems?.forEach((productItem, i) => {
      // Get all product swatches, ignore the swatch from popup selection - quick add to cart
      const productSwatches = Array.from(
        productItem?.querySelectorAll('.boost-sd__product-swatch') || []
      ).filter((swatch) => !swatch.closest('.boost-sd__popup-select-option'));

      const productImageElement = productItem?.querySelector('.boost-sd__product-image img');
      const productImage = productImageElement ? productImageElement.src : null;

      const { images, options_with_values, variantId } =
        safeParseJSON(productItem.getAttribute('data-product')) || {};

      productSwatches?.forEach((swatch, index) => {
        // hover | click | none
        const changeProductImageOn = swatches[index]?.changeProductImageOn || 'hover';

        if (changeProductImageOn === 'none') return;

        const event = changeProductImageOn === 'hover' ? 'mouseenter' : 'click';

        const defaultImages = variantId ? [{ src: productImage }] : images;
        if (event === 'mouseenter') handleMouseLeave(swatch, productItem, defaultImages);

        const swatchOptions = swatch?.querySelectorAll('.boost-sd__product-swatch-option');

        swatchOptions?.forEach((option) =>
          registerSwatchOptionEvent(
            event,
            productItem,
            swatch,
            option,
            options_with_values,
            images,
            defaultImages
          )
        );
      });

      applySwatchSettings(
        productItem,
        options_with_values,
        context.app.filterSettings.swatch_settings
      );

      initSwatchTooltipScrollHandler(productItem);
    });
  }
};

/**
 * Get enabled swatches from themeSettings
 * @type {import('./index.d').getEnabledSwatches}
 */
const getEnabledSwatches = (context) => {
  const productInfoElements = getThemeSettings(context)?.productItems?.productInfo?.elements || {};

  const swatches = [];

  ['swatches.1', 'swatches.2', 'swatches.3'].forEach((swatchNumber) => {
    if (swatchNumber in productInfoElements && productInfoElements[swatchNumber].isEnable) {
      swatches.push(productInfoElements[swatchNumber]);
    }
  });

  return swatches;
};

/**
 * Set product image to default
 * @type {import('./index.d').setProductImageToDefault}
 */
const setProductImageToDefault = (productItem, images) => {
  if (!productItem || !images?.length) return;

  const productImages = productItem.querySelectorAll('.boost-sd__product-image img');
  if (!productImages.length) return;

  const [firstImage, secondImage] = images;
  const hasSecondImage = images.length > 1;

  const generateSrcSet = (imageUrl, widths) => {
    return widths.map((width) => `${imageUrl}?width=${width} ${width}w`).join(', ');
  };

  const setProductImage = (index, image) => {
    if (productImages[index] && image) {
      // Only set product image if the new image is not the same as the current image to avoid flickering
      if (!productImages[index].src?.includes(image.src)) {
        productImages[index].src = image.src;

        const widths = [200, 300, 400, 500, 700];
        productImages[index].srcset = generateSrcSet(image.src, widths);
      }
    }
  };

  setProductImage(0, firstImage);

  // Only set the second image if both the image element and the image data exist
  if (productImages.length > 1 && hasSecondImage) {
    setProductImage(1, secondImage);
  }
};

/**
 * Handle mouse leave event of swatch option
 */
/**
 * @type {import('./index.d').handleMouseLeave}
 */
const handleMouseLeave = (swatch, productItem, images) => {
  swatch?.addEventListener('mouseleave', () => {
    if (window.boostSDKeepVariantOnHoverOutside) return;

    const previousLabel = swatch?.querySelector('.boost-sd__radio-label--selected');

    previousLabel?.classList.remove('boost-sd__radio-label--selected');
    setProductImageToDefault(productItem, images);
  });
};

/**
 * Register click or hover event for swatch option
 */
/**
 * @type {import('./index.d').registerSwatchOptionEvent}
 */
const registerSwatchOptionEvent = (
  event,
  productItem,
  swatch,
  option,
  options_with_values,
  images,
  defaultImages
) => {
  let cleanupOnClickOutside;

  option?.addEventListener(event, () => {
    handleSwatchOption({ productItem, swatch, option, options_with_values, images, defaultImages });

    if (event === 'click') {
      if (typeof cleanupOnClickOutside === 'function') cleanupOnClickOutside();

      const callback = (target) => {
        if (window.boostSDKeepVariantOnClickOutside) return;

        const previousLabel = swatch?.querySelector('.boost-sd__radio-label--selected');
        previousLabel?.classList.remove('boost-sd__radio-label--selected');

        // Set product image to default only if clicked target is not a swatch option
        if (!target?.closest('.boost-sd__radio-label')) {
          setProductImageToDefault(productItem, defaultImages);
        }
      };

      cleanupOnClickOutside = handleClickOutside(swatch, callback);
    }
  });

  // Add keyboard event listeners
  option?.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' || e.key === ' ') {
      handleSwatchOption({ productItem, swatch, option, options_with_values, images });
    }
  });
};

/**
 * Handle swatch option including set corresponding product variant image and selected label
 */
/**
 * @type {import('./index.d').handleSwatchOption}
 */
const handleSwatchOption = ({
  productItem,
  swatch,
  option,
  options_with_values,
  images,
  defaultImages,
}) => {
  const swatchType = option
    .querySelector('.boost-sd__radio-input')
    ?.getAttribute('data-swatch-type');
  const variantOption = options_with_values?.find(
    (option) => option.name === swatchType || option.original_name === swatchType
  );

  const setProductImage = (position) => {
    const productImages = productItem.querySelectorAll('.boost-sd__product-image img');

    if (!productImages || productImages.length === 0) return;

    productImages.forEach((img) => {
      const currentPosition = img.classList.contains('boost-sd__product-image-img--main')
        ? position
        : position + 1;
      const matchedImage = images?.find((item) => item.position === currentPosition)?.src;

      if (matchedImage && img.src !== matchedImage) {
        img.src = matchedImage;
        img.srcset = matchedImage;
      }
    });
  };

  const setSelectedLabel = (label) => {
    // Remove previous selected
    const previousLabel = swatch?.querySelector('.boost-sd__radio-label--selected');
    previousLabel?.classList.remove('boost-sd__radio-label--selected');

    // Mark as selected
    label?.classList.add('boost-sd__radio-label--selected');
  };

  if (variantOption) {
    const radioInput = option.querySelector('.boost-sd__radio-input');
    const radioInputValue = radioInput?.getAttribute('value');
    const variantValue = variantOption?.values?.find((value) => value.title === radioInputValue);

    if (variantValue?.image) {
      setProductImage(variantValue.image);
    } else {
      // Variant option has no image, fall back to first and second of images
      setProductImageToDefault(productItem, defaultImages);
    }
  }

  setSelectedLabel(option.querySelector('.boost-sd__radio-label'));
};

// Move processedLabels to module scope to persist across calls
const processedLabels = new WeakSet();

/**
 * @public
 * Add scroll handler to all product swatch tooltips
 * @type {import('./index.d').initSwatchTooltipScrollHandler}
 */
export const initSwatchTooltipScrollHandler = (container) => {
  const swatchBlocks = container.querySelectorAll('.boost-sd__product-swatch');
  swatchBlocks.forEach((swatchBlock) => {
    const options = swatchBlock.querySelectorAll('.boost-sd__product-swatch-option');
    options.forEach((option) => {
      const label = option.querySelector('.boost-sd__radio-label');
      const tooltip = option.querySelector('.boost-sd__tooltip-content');
      if (!label || !tooltip || processedLabels.has(label)) return;

      const attachTooltipScrollHandler = () => {
        addSwatchTooltipScrollHandler(tooltip);
      };

      label.addEventListener('touchstart', attachTooltipScrollHandler, { passive: true });
      label.addEventListener('click', attachTooltipScrollHandler);

      // Add label to processedLabels to prevent duplicate listeners
      processedLabels.add(label);
    });
  });
};

/**
 * @public
 * Add scroll handler to hide tooltip content for product swatch
 * @type {import('./index.d').addSwatchTooltipScrollHandler}
 */
export const addSwatchTooltipScrollHandler = (tooltipContent) => {
  if (!tooltipContent) return;

  // Use a stable handler reference to allow proper removeEventListener
  if (!tooltipContent._hideOnScrollHandler) {
    tooltipContent._hideOnScrollHandler = function hideOnScroll() {
      tooltipContent.style.display = 'none';
      window.removeEventListener('scroll', tooltipContent._hideOnScrollHandler);
    };
  }
  // Always remove before adding to avoid duplicates
  window.removeEventListener('scroll', tooltipContent._hideOnScrollHandler);
  window.addEventListener('scroll', tooltipContent._hideOnScrollHandler, { passive: true });
};

/**
 * @type {import('./index.d').applySwatchSettings}
 */
export const applySwatchSettings = (container, options_with_values, swatch_settings) => {
  if (!swatch_settings || typeof swatch_settings !== 'object') return;
  const swatchBlocks = container.querySelectorAll('.boost-sd__product-swatch');

  swatchBlocks.forEach((swatchBlock) => {
    const optionNodes = swatchBlock.querySelectorAll('.boost-sd__product-swatch-option');

    optionNodes.forEach((processOptionNode) => {
      const input = processOptionNode.querySelector('input.boost-sd__radio-input');
      const label = processOptionNode.querySelector('label.boost-sd__radio-label');
      const tooltip = processOptionNode.querySelector('.boost-sd__tooltip-content');
      const swatchBy = processOptionNode.getAttribute('data-swatch-by');
      if (!input || !label) return;

      const rawValue = input.value || '';
      const value = rawValue.toLowerCase();
      const swatchObj = swatch_settings[value] || swatch_settings[`${SWATCH_PREFIX}${value}`];
      if (!swatchObj) return;

      // Handler logic
      if (swatchBy === SWATCH_OPTION_TYPE.TEXT) {
        handleTextSwatch(label, tooltip, swatchObj);
      } else if (swatchBy === SWATCH_OPTION_TYPE.PRODUCT_IMAGE) {
        handleProductImageSwatch(label, swatchObj);
      } else if (swatchBy === SWATCH_OPTION_TYPE.COLOR_IMAGE) {
        handleColorImageSwatch(label, swatchObj);
      }
    });
  });
};

// Handle displaying text for swatch
export const handleTextSwatch = (label, tooltip, swatchObj) => {
  if (swatchObj.text) {
    label.textContent = swatchObj.text;
    if (tooltip) tooltip.textContent = swatchObj.text;
  }
};

// Handle product image swatch
export const handleProductImageSwatch = (label, swatchObj) => {
  if (isBackgroundImageEmpty(label) && swatchObj.imageUrl) {
    label.style.backgroundImage = `url('${swatchObj.imageUrl}')`;
  } else if (
    isBackgroundImageEmpty(label) &&
    Array.isArray(swatchObj.colorCodes) &&
    swatchObj.colorCodes.length
  ) {
    applyColorToSwatch(label, swatchObj.colorCodes);
  }
};

// Handle color and image swatch
export const handleColorImageSwatch = (label, swatchObj) => {
  const hasColorCodes = Array.isArray(swatchObj.colorCodes) && swatchObj.colorCodes.length > 0;

  if (swatchObj.imageUrl) {
    label.style.backgroundImage = `url('${swatchObj.imageUrl}')`;

    if (hasColorCodes) {
      label.style.backgroundColor = swatchObj.colorCodes[0];
    }
  } else if (hasColorCodes) {
    applyColorToSwatch(label, swatchObj.colorCodes);
  }
};

export function applyColorToSwatch(label, colorCodes) {
  if (Array.isArray(colorCodes)) {
    const [color1, color2] = colorCodes;
    if (colorCodes.length > 1 && color1 && color2) {
      label.style.backgroundImage = `linear-gradient(to top left, ${color2} 50%, ${color1} 50%)`;
      label.style.backgroundColor = '';
    } else if (colorCodes.length === 1 && color1) {
      label.style.backgroundColor = color1;
      label.style.backgroundImage = '';
    } else {
      label.style.backgroundImage = '';
      label.style.backgroundColor = '';
    }
  }
}

export function isBackgroundImageEmpty(label) {
  return (
    !(label.style.backgroundImage && label.style.backgroundImage !== 'none') &&
    (!window.getComputedStyle(label).backgroundImage ||
      window.getComputedStyle(label).backgroundImage === 'none')
  );
}
